/**
 * MatchExplanationPopover.js — RC 1.28 Decision Transparency.
 *
 * Tiny popover that renders the `preference_scoring.explanation`
 * block + the score arithmetic. Deliberately compact — this is a
 * "Why this match?" tooltip, not a modal. Attaches to an inline
 * trigger (a question-mark button next to the match label) so
 * each job card stays clean by default.
 *
 * Props:
 *   preferenceScoring  (object, required)
 *     The full `preference_scoring` dict from `/api/jobs?use_preferences=true`.
 *     Exactly: { base_score, location_multiplier, final_score,
 *                match_label, explanation: { location_match, job_location,
 *                preferred_regions, location_strictness, open_to_relocation,
 *                reason } }.
 *
 *   triggerTestId  (string, required)
 *     data-testid for the button trigger so Playwright can click
 *     per-job without collisions.
 */
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { HelpCircle } from 'lucide-react';

import {
  humaniseStrictness, humaniseRelocation, shouldRenderExplanation,
} from './matchExplanationHelpers';

export default function MatchExplanationPopover({
  preferenceScoring,
  triggerTestId,
}) {
  const ps = preferenceScoring;
  if (!shouldRenderExplanation(ps)) return null;
  const e = ps.explanation;
  const hasScores = ps.base_score !== null && ps.final_score !== null;

  // The spec's preferred copy example, populated from the response:
  //   "Base fit: 82"
  //   "Location adjustment: ×0.7"
  //   "Reason: …"
  //   "Final: 57.4 → Good match (relocation required)"
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center h-5 w-5 rounded-full text-ndor-ink-muted hover:text-ndor-ink hover:bg-ndor-surface-alt/80 transition-colors"
          aria-label="Why this match?"
          title="Why this match?"
          data-testid={triggerTestId}
          onClick={(ev) => ev.stopPropagation()}
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className="w-[320px] text-xs p-3 space-y-2.5"
        data-testid={`${triggerTestId}-content`}
      >
        <div className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted">
          Why this match?
        </div>

        {hasScores && (
          <div
            className="font-mono text-[11px] text-ndor-ink space-y-0.5"
            data-testid={`${triggerTestId}-math`}
          >
            <div>Base fit: <span className="font-semibold tabular-nums">{ps.base_score}</span></div>
            <div>
              Location adjustment:{' '}
              <span className="font-semibold tabular-nums">
                ×{ps.location_multiplier}
              </span>
            </div>
            <div>
              Final:{' '}
              <span className="font-semibold tabular-nums">{ps.final_score}</span>
              {' → '}
              <span className="font-semibold">{ps.match_label}</span>
            </div>
          </div>
        )}
        {!hasScores && (
          <div className="text-[11px] text-ndor-ink-muted italic">
            Not analysed yet — run Analyse Fit to see a base score.
          </div>
        )}

        <div
          className="pt-2 border-t border-ndor-edge text-[11px] text-ndor-ink leading-relaxed"
          data-testid={`${triggerTestId}-reason`}
        >
          <span className="font-medium">Reason:</span>{' '}
          {e.reason || '—'}
        </div>

        <dl className="text-[10px] text-ndor-ink-muted grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 pt-1">
          <dt>Job location</dt>
          <dd className="text-ndor-ink truncate" title={e.job_location || ''}>
            {e.job_location || <span className="italic">unspecified</span>}
          </dd>
          <dt>Preferred</dt>
          <dd className="text-ndor-ink">
            {e.preferred_regions && e.preferred_regions.length > 0
              ? e.preferred_regions.join(', ')
              : <span className="italic">none set</span>}
          </dd>
          <dt>Strictness</dt>
          <dd className="text-ndor-ink">{humaniseStrictness(e.location_strictness)}</dd>
          <dt>Relocation</dt>
          <dd className="text-ndor-ink">{humaniseRelocation(e.open_to_relocation)}</dd>
          <dt>Matched?</dt>
          <dd
            className={e.location_match ? 'text-ndor-success' : 'text-ndor-ink-muted'}
            data-testid={`${triggerTestId}-matched`}
          >
            {e.location_match ? 'Yes' : 'No'}
          </dd>
        </dl>
      </PopoverContent>
    </Popover>
  );
}

export { humaniseStrictness, humaniseRelocation };

